import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { GiDungeonGate } from '../icons/startupGameIcons';
import { WebSocketContext } from './WebSocketProvider';
import { switchActiveCampaign } from './useActiveCampaign';

function SessionStartNotification() {
    const { stompClient } = useContext(WebSocketContext);
    const navigate = useNavigate();
    const userJSON = JSON.parse(sessionStorage.getItem('user'));
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        if (!stompClient || !userJSON) return;

        const headers = { 'Authorization': 'Bearer ' + userJSON.accessToken };
        let subs = [];
        let cancelled = false;

        fetch(`${process.env.REACT_APP_API_PROXY}/campaigns/getAllCampaignsForUser`, { headers })
            .then(res => res.json())
            .then(campaigns => {
                if (cancelled || !Array.isArray(campaigns)) return;
                subs = campaigns.map(campaign =>
                    stompClient.subscribe(`/topic/campaign/${campaign.id}`, (payload) => {
                        try {
                            const data = JSON.parse(payload.body);
                            const msg = data.body ?? data;
                            if (msg.status !== 'SESSION_STARTED' || msg.senderName === userJSON.username) return;
                            const detail = JSON.parse(msg.message);
                            const currentId = JSON.parse(localStorage.getItem(`selectedCampaignId_${userJSON.username}`) || 'null');
                            if (window.location.pathname === '/tabletop' && currentId === detail.campaignId) return;
                            setNotification(prev => prev ?? {
                                campaign,
                                campaignId: detail.campaignId,
                                campaignName: detail.campaignName ?? campaign.campaignName,
                                partyId: detail.partyId ?? null,
                            });
                        } catch {}
                    })
                );
            })
            .catch(() => {});

        return () => {
            cancelled = true;
            subs.forEach(s => s?.unsubscribe());
        };
    }, [stompClient]); // eslint-disable-line react-hooks/exhaustive-deps

    // Joining makes the session's campaign the active one on the backend before the tabletop
    // loads: the tabletop opens the backend's active campaign, not just this browser's pick.
    async function handleJoin() {
        if (!notification || !userJSON) return;
        const { campaign, campaignId, campaignName } = notification;
        setNotification(null);
        await switchActiveCampaign({ ...campaign, id: campaignId, campaignName });
        window.location.href = '/tabletop';
    }

    function handleDismiss() {
        setNotification(null);
    }

    if (!notification) return null;

    return (
        <Dialog
            open
            PaperProps={{
                sx: {
                    backgroundColor: 'var(--lm-bg-1a1f2b)',
                    color: 'var(--lm-tx-f5f5f5)',
                    border: '1px solid var(--th-6c4f9b)',
                    borderRadius: 2,
                    minWidth: 320,
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
                <GiDungeonGate size={24} color="var(--lm-tx-th-c9a0f0)" />
                Session Started
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: 'rgba(var(--lm-tx-f5f5f5-rgb), var(--lm-tx-a85))' }}>
                    The GM has started a session for{' '}
                    <strong style={{ color: 'var(--lm-tx-th-c9a0f0)' }}>{notification.campaignName}</strong>.
                    Would you like to join?
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
                <Button onClick={handleDismiss} sx={{ color: 'var(--lm-tx-th-9e8ab5)' }}>
                    Dismiss
                </Button>
                <Button
                    onClick={handleJoin}
                    variant="contained"
                    sx={{ backgroundColor: 'var(--th-6c4f9b)', '&:hover': { backgroundColor: 'var(--th-7b5fb5)' } }}
                >
                    Join Session
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default SessionStartNotification;